(() => {
  if (window.UNKWNHavocOffer) return;

  const seenOffers = new WeakSet();
  const metaPixelId = '1362179649237891';
  const pendingMetaEvents = [];
  let metaPixelInitialized = false;
  let privacyApiUnavailable = false;
  let privacyListenerRegistered = false;
  const metaEventNames = {
    view: 'HavocOfferView',
    select: 'HavocOfferSelect',
    deselect: 'HavocOfferDeselect',
    fitment_open: 'HavocFitmentOpen',
    accept: 'HavocOfferAccept',
  };

  function metaPixelIsInitialized() {
    try {
      const pixels = window.fbq?.getState?.()?.pixels || [];
      return pixels.some((pixel) => String(pixel.id || pixel.pixelID || pixel.pixelId || '') === metaPixelId);
    } catch (_) {
      return false;
    }
  }

  function ensureMetaPixel() {
    if (typeof window.fbq !== 'function') return false;
    if (metaPixelInitialized || metaPixelIsInitialized()) {
      metaPixelInitialized = true;
      return true;
    }

    try {
      window.fbq('init', metaPixelId);
      metaPixelInitialized = true;
      return true;
    } catch (_) {
      return false;
    }
  }

  function metaProcessingAllowed() {
    if (privacyApiUnavailable) return true;

    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return null;

    try {
      const analyticsAllowed = privacy.analyticsProcessingAllowed?.() !== false;
      const marketingAllowed = privacy.marketingAllowed?.() !== false;
      const saleOfDataAllowed = privacy.saleOfDataAllowed?.() !== false;
      return analyticsAllowed && marketingAllowed && saleOfDataAllowed;
    } catch (_) {
      return false;
    }
  }

  function consentWasExplicitlyDenied() {
    try {
      const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.() || {};
      return consent.analytics === 'no' || consent.marketing === 'no' || consent.sale_of_data === 'no';
    } catch (_) {
      return false;
    }
  }

  function sendMetaEvent(event) {
    if (!ensureMetaPixel()) return false;

    try {
      window.fbq('trackCustom', event.name, event.payload);
      return true;
    } catch (_) {
      return false;
    }
  }

  function flushMetaEvents() {
    if (metaProcessingAllowed() !== true) return;

    while (pendingMetaEvents.length) {
      const event = pendingMetaEvents.shift();
      if (sendMetaEvent(event)) continue;
      pendingMetaEvents.unshift(event);
      break;
    }
  }

  function handlePrivacyState() {
    const allowed = metaProcessingAllowed();
    if (allowed === true) flushMetaEvents();
    else if (allowed === false && consentWasExplicitlyDenied()) pendingMetaEvents.length = 0;
  }

  function queueMetaEvent(name, payload) {
    pendingMetaEvents.push({ name, payload });
    if (pendingMetaEvents.length > 20) pendingMetaEvents.shift();

    const allowed = metaProcessingAllowed();
    if (allowed === true) {
      flushMetaEvents();
    } else if (allowed === false && consentWasExplicitlyDenied()) {
      pendingMetaEvents.length = 0;
    }
  }

  function initializeCustomerPrivacy() {
    if (!privacyListenerRegistered) {
      privacyListenerRegistered = true;
      document.addEventListener('visitorConsentCollected', handlePrivacyState);
    }

    if (window.Shopify?.customerPrivacy) {
      handlePrivacyState();
      return;
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      privacyApiUnavailable = true;
      flushMetaEvents();
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error || !window.Shopify?.customerPrivacy) privacyApiUnavailable = true;
        handlePrivacyState();
      }
    );
  }

  function track(eventName, detail = {}) {
    const payload = {
      event: `unkwn_havoc_${eventName}`,
      havoc_offer_placement: detail.placement || 'unknown',
      havoc_discount_percent: Number(detail.discountPercent || 0),
      havoc_variant_id: String(detail.variantId || ''),
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    const metaEventName = metaEventNames[eventName];
    if (metaEventName) {
      const metaPayload = {
        offer_placement: payload.havoc_offer_placement,
        discount_percent: payload.havoc_discount_percent,
        content_type: 'product',
      };
      if (payload.havoc_variant_id) metaPayload.content_ids = [payload.havoc_variant_id];
      queueMetaEvent(metaEventName, metaPayload);
    }

    const shopifyAnalytics = window.Shopify?.analytics;
    if (typeof shopifyAnalytics?.publish === 'function') {
      try {
        const published = shopifyAnalytics.publish(`unkwn:havoc_${eventName}`, {
          placement: payload.havoc_offer_placement,
          discountPercent: payload.havoc_discount_percent,
          variantId: payload.havoc_variant_id,
        });
        if (published?.catch) published.catch(() => {});
      } catch (_) {
        // A tracking receiver must never interrupt the buying journey.
      }
    }

    window.dispatchEvent(new CustomEvent(`unkwn:havoc:${eventName}`, { detail: payload }));
  }

  function registerView(offer) {
    if (seenOffers.has(offer)) return;

    const record = () => {
      if (seenOffers.has(offer) || offer.hidden) return;
      seenOffers.add(offer);
      track('view', {
        placement: offer.dataset.placement,
        discountPercent: offer.dataset.discountPercent,
        variantId: offer.dataset.havocVariantId,
      });
    };

    if (!('IntersectionObserver' in window)) {
      record();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) {
          record();
          observer.disconnect();
        }
      },
      { threshold: [0.35] }
    );
    observer.observe(offer);
  }

  function updateProductChoice(checkbox) {
    const offer = checkbox.closest('[data-havoc-offer]');
    const productForm = checkbox.closest('product-form');
    const submitText = productForm?.querySelector('.product-form__submit > span');
    const expressCheckout = productForm?.querySelector('.shopify-payment-button');
    const checkoutNote = offer?.querySelector('[data-havoc-checkout-note]');
    const controlNote = offer?.querySelector('[data-havoc-control-note]');

    if (!offer || !submitText) return;
    if (!submitText.dataset.havocDefaultLabel) submitText.dataset.havocDefaultLabel = submitText.textContent.trim();

    offer.classList.toggle('is-selected', checkbox.checked);
    submitText.textContent = checkbox.checked
      ? offer.dataset.checkedButtonLabel
      : submitText.dataset.havocDefaultLabel;

    if (controlNote) {
      controlNote.textContent = checkbox.checked
        ? controlNote.dataset.checkedLabel
        : controlNote.dataset.uncheckedLabel;
    }

    if (expressCheckout) expressCheckout.hidden = checkbox.checked;
    if (checkoutNote) checkoutNote.hidden = !checkbox.checked;
  }

  function hideLegacyProductCard(root = document) {
    const productOffer = root.querySelector?.('[data-havoc-offer][data-placement="product_page"]');
    if (!productOffer) return;

    const variantId = String(productOffer.dataset.havocVariantId || '');
    root.querySelectorAll?.('a[href*="/cart/add"]').forEach((link) => {
      if (link.closest('[data-havoc-offer]')) return;

      let linkVariantId = '';
      try {
        linkVariantId = new URL(link.href, window.location.origin).searchParams.get('id') || '';
      } catch (_) {
        return;
      }
      if (linkVariantId !== variantId) return;

      const legacyCard = link.parentElement;
      if (legacyCard?.textContent.includes('Goes with your clipper') || legacyCard?.textContent.includes('Goes with the clipper in this bundle')) {
        legacyCard.hidden = true;
        legacyCard.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function init(root = document) {
    hideLegacyProductCard(root);
    root.querySelectorAll('[data-havoc-offer]').forEach(registerView);
    root.querySelectorAll('[data-havoc-addon-toggle]').forEach(updateProductChoice);
  }

  document.addEventListener('change', (event) => {
    const checkbox = event.target.closest?.('[data-havoc-addon-toggle]');
    if (!checkbox) return;

    const offer = checkbox.closest('[data-havoc-offer]');
    updateProductChoice(checkbox);
    track(checkbox.checked ? 'select' : 'deselect', {
      placement: offer?.dataset.placement,
      discountPercent: offer?.dataset.discountPercent,
      variantId: offer?.dataset.havocVariantId,
    });
  });

  document.addEventListener('toggle', (event) => {
    const fitment = event.target.closest?.('[data-havoc-fitment]');
    if (!fitment?.open) return;
    const offer = fitment.closest('[data-havoc-offer]');
    track('fitment_open', {
      placement: offer?.dataset.placement || fitment.dataset.placement,
      discountPercent: offer?.dataset.discountPercent || fitment.dataset.discountPercent,
      variantId: offer?.dataset.havocVariantId || fitment.dataset.havocVariantId,
    });
  }, true);

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest?.('[data-havoc-cart-add]');
    if (!cartLink) return;
    const offer = cartLink.closest('[data-havoc-offer]');
    track('accept', {
      placement: offer?.dataset.placement || 'cart',
      discountPercent: offer?.dataset.discountPercent,
      variantId: offer?.dataset.havocVariantId,
    });
  });

  window.UNKWNHavocOffer = { init, track };
  initializeCustomerPrivacy();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
